import { create } from 'zustand';
import { SupportSession } from '../types/admin';

const STORAGE_KEY = 'mytottot_support_session';

interface SupportSessionState {
  session: SupportSession | null;
  isActive: boolean;

  // Actions
  setSession: (session: SupportSession) => void;
  clearSession: () => void;
  getToken: () => string | null;
  isSessionValid: () => boolean;
}

// Load session from sessionStorage
function loadSession(): SupportSession | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const session = JSON.parse(stored) as SupportSession;

    // Check if expired
    if (new Date(session.expiresAt) <= new Date()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

// Save session to sessionStorage
function saveSession(session: SupportSession): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

// Check if session is valid (not expired)
function isValid(session: SupportSession | null): boolean {
  if (!session) return false;
  return new Date(session.expiresAt) > new Date();
}

export const useSupportSession = create<SupportSessionState>((set, get) => ({
  session: loadSession(),
  isActive: isValid(loadSession()),

  setSession: (session: SupportSession) => {
    saveSession(session);
    set({ session, isActive: true });
  },

  clearSession: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    set({ session: null, isActive: false });
  },

  getToken: () => {
    const { session } = get();
    if (!session) return null;

    // Check expiry
    if (new Date(session.expiresAt) <= new Date()) {
      get().clearSession();
      return null;
    }

    return session.supportToken;
  },

  isSessionValid: () => {
    const { session } = get();
    return isValid(session);
  },
}));

// Helper hook for getting session info
export function useActiveSupportSession() {
  const session = useSupportSession((state) => state.session);
  const isActive = useSupportSession((state) => state.isActive);
  const isSessionValid = useSupportSession((state) => state.isSessionValid);

  return {
    session,
    isActive: isActive && isSessionValid(),
    targetUserId: session?.targetUserId ?? null,
    ticketId: session?.ticketId ?? null,
    expiresAt: session?.expiresAt ? new Date(session.expiresAt) : null,
  };
}

// Get support token for API requests (used by api.ts)
export function getSupportToken(): string | null {
  return useSupportSession.getState().getToken();
}
